import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '../../i18n';
import { monthName } from '../../lib/format';
import { useClientApp } from '../context';
import { usePage, type Crumb } from '../page';
import { belegePath, CATEGORY_LABEL } from './labels';
import type { Category } from './types';

// Titel, Pfad und Zurück-Ziel einer Belege-Ebene:
// Start › Belege › <Firma> › <Jahr> › <Monat> › <Ordner>. Die letzte Ebene ist nicht klickbar.
// Mit nur einer Firma gibt es die Firmenwahl nicht, „Belege“ ist dann kein Link und Zurück springt zur Übersicht.
export function useBelegePage(p: {
  title: string;
  clientId?: string;
  clientName?: string;
  year?: number;
  month?: number;
  category?: Category;
}) {
  const { clients, clientName } = useClientApp();
  const one = clients.length === 1;
  const levels: Crumb[] = [
    { label: t('Start'), to: '/app' },
    { label: t('Finanzbuchhaltung'), to: one ? undefined : '/app/belege' },
  ];
  if (p.clientId) levels.push({ label: p.clientName ?? clientName(p.clientId), to: belegePath(p.clientId) });
  if (p.clientId && p.year) levels.push({ label: String(p.year), to: belegePath(p.clientId, p.year) });
  if (p.clientId && p.year && p.month) levels.push({ label: monthName(p.month), to: belegePath(p.clientId, p.year, p.month) });
  if (p.clientId && p.year && p.month && p.category) {
    levels.push({ label: t(CATEGORY_LABEL[p.category]), to: belegePath(p.clientId, p.year, p.month, p.category) });
  }
  const last = levels.length - 1;
  const crumbs = levels.map((c, i) => (i === last ? { label: c.label } : c));
  // eine Ebene höher; über die Firmenwahl hinweg, wenn es sie nicht gibt
  let back = levels[last - 1]?.to ?? '/app';
  if (last === 2 && one) back = '/app';
  usePage({ title: p.title, crumbs, back, nav: 'belege' });
}

// Fenster mit eigenem Eintrag im Browser-Verlauf (gleiche Adresse, Merker im state): Die Zurück-Geste nimmt
// den Eintrag weg und schließt damit nur das Fenster, die Seite darunter bleibt (Spezifikation Abschnitt 4).
// Der Merker ist je Öffnen eindeutig, damit ein alter Eintrag (Neuladen, „Vor“) das Fenster nicht wieder öffnet.
export function useHistoryWindow(name: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useRef<string | null>(null);
  const state = location.state as Record<string, unknown> | null;
  const isOpen = token.current !== null && state?.[name] === token.current;
  const open = () => {
    token.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { state: { ...state, [name]: token.current } },
    );
  };
  // selbst schließen: den eigenen Eintrag wieder wegnehmen (nach der Zurück-Geste ist er schon weg)
  const close = () => {
    if (isOpen) navigate(-1);
    token.current = null;
  };
  return { isOpen, open, close };
}
